import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Extent from '@arcgis/core/geometry/Extent';
import type Polygon from '@arcgis/core/geometry/Polygon';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { ComboboxField } from 'components/DesignSystems/ComboboxField/ComboboxField';
import {
  fetchSitrepById,
  isSitrepSectionConflictError,
  keys as sitrepKeys,
  useSitrepV2,
  useUpdateSitrepSectionV2,
  type SitrepSectionConflictDetails,
  type SitrepSectionType,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { ArcGisConnectButton } from 'components/MapPage/CorporateViewTabs/LayersTab/ArcGisConnectButton';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { getPortalUrl } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import { syncExternalLayers } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/syncExternalLayers';
import type { ArcGisAuthOptions } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/externalLayerLoader';
import { geoJsonToArcgisPolygon } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/geoJsonToArcgisPolygon';
import { zoomMapViewToLocation } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/mapViewZoom';
import { queryExternalLayerFeatures } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/queryExternalLayerFeatures';
import { WGS84_SPATIAL_REFERENCE } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import SitrepSectionConflictModal from 'components/Reports/SitrepSectionConflictModal';
import useZoomToReportAor from 'components/Reports/hooks/useZoomToReportAor';
import { useAors } from 'hooks/useAors';
import { observability } from 'utils/observability';
import { useEnvironmentVariables } from 'utils/useEnvironmentVariables';
import { LayerAnalyticsCard } from './LayerAnalyticsCard';
import {
  formatFeatureCount,
  getLayerIdFromValue,
  NO_BOUNDARY_MESSAGE,
  NO_FEATURES_MESSAGE,
  parseLayerAnalyticsContent,
  resolveReportBoundaryGeoJson,
  toSelectedLayer,
  type LayerAnalyticsContent,
  type LayerAnalyticsFeature,
  type LayerAnalyticsResult,
  type LayerAnalyticsSelectedLayer,
} from './layerAnalytics.utils';
import {
  buildLayerAnalyticsResult,
  serializeLayerAnalyticsContent,
} from './layerAnalyticsResult';
import type { ZoomTarget, ZoomToTarget } from './layerAnalyticsZoomColumn';
import {
  useHazardLayerOptions,
  type HazardLayerOption,
} from './useHazardLayerOptions';

const getDefaultAuthType = (layer: LayerAnalyticsSelectedLayer) =>
  layer.authType ?? 'userAuth';

export type LayerAnalyticsSectionProps = {
  section: SitrepSectionType;
  sitrepId?: string;
  canEdit?: boolean;
  onUpdated?: () => void;
  readOnly?: boolean;
};

type LayerAnalyticsResultsProps = {
  results: LayerAnalyticsResult[];
  emptyMessage: string;
  /** Enables the map-icon columns (editor with a map only). */
  onZoomToTarget?: ZoomToTarget;
  canZoomToTarget?: boolean;
};

const LayerAnalyticsResults: React.FC<LayerAnalyticsResultsProps> = ({
  results,
  emptyMessage,
  onZoomToTarget,
  canZoomToTarget = false,
}) => {
  if (results.length === 0) {
    return <em className="text-muted">{emptyMessage}</em>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {results.map((result) => (
        <div key={result.layerValue}>
          <h4 className="fs-6 fw-bold mb-2">
            {result.label} ({formatFeatureCount(result.featureCount)})
          </h4>
          {result.cards.length === 0 ? (
            <em className="text-muted">{NO_FEATURES_MESSAGE}</em>
          ) : (
            <div className="d-flex flex-column gap-3">
              {result.cards.map((card) => (
                <LayerAnalyticsCard
                  key={card.key}
                  card={card}
                  rowIdPrefix={result.layerValue}
                  onZoomToTarget={onZoomToTarget}
                  canZoomToTarget={canZoomToTarget}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

type LayerAnalyticsEditorProps = {
  section: SitrepSectionType;
  sitrepId: string;
  savedContent: LayerAnalyticsContent;
  onUpdated?: () => void;
};

const LayerAnalyticsEditor: React.FC<LayerAnalyticsEditorProps> = ({
  section,
  sitrepId,
  savedContent,
  onUpdated,
}) => {
  const { isMapVisible, setPreviewLayers, mapView } = useUscgMapContext();
  const group = useCurrentGroup();
  const { data: envData } = useEnvironmentVariables();
  const { options, isLoading: isLoadingOptions } = useHazardLayerOptions();
  const { mutateAsync: updateSection, isPending: isSaving } =
    useUpdateSitrepSectionV2();

  // Hazard features are limited to the report's boundary: the AOR polygon for
  // AOR reports, the incident location polygon for incident reports.
  const { data: sitrep, isLoading: isLoadingSitrep } = useSitrepV2(sitrepId);
  const { data: aors, isLoading: isLoadingAors } = useAors();
  const { data: incidents, isLoading: isLoadingIncidents } = useAllIncidents();
  const isLoadingBoundary =
    isLoadingSitrep || isLoadingAors || isLoadingIncidents;
  const boundary = useMemo<Polygon | null>(() => {
    const geoJson = resolveReportBoundaryGeoJson({
      aorId: sitrep?.aor_id,
      incidentId: sitrep?.incident_id,
      aors: aors ?? [],
      incidents: incidents ?? [],
    });
    return geoJson ? geoJsonToArcgisPolygon(geoJson) : null;
  }, [aors, incidents, sitrep?.aor_id, sitrep?.incident_id]);

  const [selectedLayers, setSelectedLayers] = useState<
    LayerAnalyticsSelectedLayer[]
  >(savedContent.selectedLayers);
  const [results, setResults] = useState(savedContent.results);
  const [ranAt, setRanAt] = useState(savedContent.ranAt);
  const [isRunning, setIsRunning] = useState(false);
  const [conflict, setConflict] = useState<SitrepSectionConflictDetails | null>(
    null
  );
  const [conflictContent, setConflictContent] =
    useState<LayerAnalyticsContent | null>(null);

  const [runError, setRunError] = useState<string | null>(null);

  const latestVersionId = section.section_versions?.[0]?.version_id;

  // Saves are serialized through a loop that is independent of the component
  // lifecycle, so content queued behind an in-flight save is still sent if the
  // editor unmounts (e.g. switching OPORD category tabs). Each save uses the
  // version_id produced by the previous one as its base.
  const queryClient = useQueryClient();
  const isSaveInFlightRef = useRef(false);
  const pendingContentRef = useRef<LayerAnalyticsContent | null>(null);
  const baseVersionIdRef = useRef<string | undefined>(latestVersionId);
  const savedContentRef = useRef(savedContent);
  savedContentRef.current = savedContent;

  const comboboxOptions = useMemo(() => {
    const optionValues = new Set(options.map((option) => option.value));
    return [
      ...options,
      ...selectedLayers.filter((layer) => !optionValues.has(layer.value)),
    ];
  }, [options, selectedLayers]);

  const fetchLatestVersionId = async () => {
    const sitrepData = await queryClient.fetchQuery({
      queryKey: sitrepKeys.byId(sitrepId),
      queryFn: () => fetchSitrepById(sitrepId),
      staleTime: 0,
    });
    return sitrepData.sections?.find(
      (sitrepSection) => sitrepSection.id === section.id
    )?.section_versions?.[0]?.version_id;
  };

  const drainSaves = async () => {
    if (isSaveInFlightRef.current) return;
    isSaveInFlightRef.current = true;
    try {
      while (pendingContentRef.current) {
        const content = pendingContentRef.current;
        pendingContentRef.current = null;
        if (
          JSON.stringify(content) === JSON.stringify(savedContentRef.current)
        ) {
          continue;
        }
        try {
          // The structured content is the source of truth; the HTML is the
          // human-readable copy used by exports and AI consumers.
          await updateSection({
            sitrepId,
            sectionId: section.id,
            baseVersionId: baseVersionIdRef.current,
            silent: true,
            updatedFields: {
              sectionText: serializeLayerAnalyticsContent(content),
              sectionData: content,
            },
          });
          baseVersionIdRef.current = await fetchLatestVersionId();
          onUpdated?.();
        } catch (error) {
          if (isSitrepSectionConflictError(error)) {
            // Content queued behind the rejected save is a newer full
            // snapshot of the editor, so it is what the user resolves.
            const latestContent = pendingContentRef.current ?? content;
            pendingContentRef.current = null;
            setConflictContent(latestContent);
            setConflict(error.conflict);
            return;
          }
          observability.captureException(error);
        }
      }
    } finally {
      isSaveInFlightRef.current = false;
    }
  };

  const saveContent = (content: LayerAnalyticsContent) => {
    pendingContentRef.current = content;
    void drainSaves();
  };

  // Follow collaborator edits picked up by polling while no local save is
  // pending; local state is re-synced to that version by the effect below.
  useEffect(() => {
    if (isSaveInFlightRef.current || pendingContentRef.current) return;
    baseVersionIdRef.current = latestVersionId;
  }, [latestVersionId]);

  // Keep local state in sync with the saved section (e.g. collaborator edits)
  // whenever no local change is pending.
  useEffect(() => {
    if (isSaveInFlightRef.current || pendingContentRef.current || isRunning) {
      return;
    }
    setSelectedLayers(savedContent.selectedLayers);
    setResults(savedContent.results);
    setRanAt(savedContent.ranAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedContent]);

  const showLayerOnMap = useCallback(
    (layer: LayerAnalyticsSelectedLayer) => {
      const previewId = layer.arcgisItemId ?? getLayerIdFromValue(layer.value);
      const authType = getDefaultAuthType(layer);
      setPreviewLayers((prev) =>
        prev.some((preview) => preview.id === previewId)
          ? prev
          : [...prev, { id: previewId, url: layer.serviceUrl, authType }]
      );
    },
    [setPreviewLayers]
  );

  const hideLayerOnMap = (layer: LayerAnalyticsSelectedLayer) => {
    const previewId = layer.arcgisItemId ?? getLayerIdFromValue(layer.value);
    setPreviewLayers((prev) =>
      prev.filter((preview) => preview.id !== previewId)
    );
  };

  // Selected layers are shown on the map only while the map is visible. This
  // runs on mount (restoring saved selections) and whenever the map becomes
  // visible, so opening the map after selecting still shows the layers.
  const selectedLayersRef = useRef(selectedLayers);
  selectedLayersRef.current = selectedLayers;
  const showLayerOnMapRef = useRef(showLayerOnMap);
  showLayerOnMapRef.current = showLayerOnMap;
  useEffect(() => {
    if (!isMapVisible) return;
    selectedLayersRef.current.forEach(showLayerOnMapRef.current);
  }, [isMapVisible]);

  // Zoom to the report's area once per map-visible transition (including
  // mount): AOR reports zoom to the AOR and turn the AOR layer on, incident
  // reports zoom to the incident polygon.
  const zoomToReportAor = useZoomToReportAor();
  const zoomToReportAorRef = useRef(zoomToReportAor);
  zoomToReportAorRef.current = zoomToReportAor;
  const hasZoomedForVisibilityRef = useRef(false);
  const aorId = sitrep?.aor_id;
  useEffect(() => {
    if (!isMapVisible) {
      hasZoomedForVisibilityRef.current = false;
      return;
    }
    if (hasZoomedForVisibilityRef.current || !mapView || isLoadingBoundary) {
      return;
    }
    hasZoomedForVisibilityRef.current = true;
    if (aorId) {
      void zoomToReportAorRef.current(aorId);
    } else if (boundary?.extent) {
      mapView
        .goTo(boundary.extent.expand(1.2), { duration: 500 })
        .catch(() => undefined);
    }
  }, [aorId, boundary, isLoadingBoundary, isMapVisible, mapView]);

  const handleSelectionChange = (next: string | string[] | null) => {
    const nextValues = Array.isArray(next) ? next : [];
    const optionByValue = new Map(
      comboboxOptions.map((option) => [option.value, option])
    );
    const nextLayers = nextValues
      .map((value) => optionByValue.get(value))
      .filter((option): option is HazardLayerOption => Boolean(option))
      .map(toSelectedLayer);

    const previousValues = selectedLayers.map((layer) => layer.value);
    if (isMapVisible) {
      nextLayers
        .filter((layer) => !previousValues.includes(layer.value))
        .forEach(showLayerOnMap);
    }
    selectedLayers
      .filter((layer) => !nextValues.includes(layer.value))
      .forEach(hideLayerOnMap);

    const nextResults = results.filter((result) =>
      nextValues.includes(result.layerValue)
    );
    setSelectedLayers(nextLayers);
    setResults(nextResults);
    saveContent({
      version: 2,
      selectedLayers: nextLayers,
      ranAt,
      results: nextResults,
    });
  };

  const handleZoomToTarget = useCallback(
    ({ longitude, latitude, extent }: ZoomTarget) => {
      if (extent && mapView) {
        const [xmin, ymin, xmax, ymax] = extent;
        mapView
          .goTo(
            new Extent({
              xmin,
              ymin,
              xmax,
              ymax,
              spatialReference: WGS84_SPATIAL_REFERENCE,
            }).expand(1.2),
            { duration: 500 }
          )
          .catch(() => undefined);
        return;
      }
      if (longitude === null || latitude === null) return;
      zoomMapViewToLocation({ mapView, longitude, latitude, zoom: 10 })?.catch(
        () => undefined
      );
    },
    [mapView]
  );

  const fetchLayerFeatures = async (
    layer: LayerAnalyticsSelectedLayer
  ): Promise<LayerAnalyticsFeature[]> => {
    // Queried straight from the ArcGIS item / service URL, so it works whether
    // or not the map is visible (hidden map = no layers synced to the map).
    const layerId = getLayerIdFromValue(layer.value);
    const authType = getDefaultAuthType(layer);
    const portalUrl = getPortalUrl(group);
    const apiKey = envData?.ARCGIS_API_KEY;
    const options: ArcGisAuthOptions | null =
      authType === 'apiKey'
        ? apiKey
          ? { accessType: 'apiKey', apiKey }
          : null
        : portalUrl
          ? { accessType: 'userAuth', portalUrl }
          : null;
    if (!options) {
      throw new Error(`No ArcGIS credentials available for ${layer.label}`);
    }

    const { layers, loadError } = await syncExternalLayers(
      {
        id: layerId,
        arcgisItemId: layer.arcgisItemId,
        serviceUrl: layer.serviceUrl,
        incident: null,
        authType,
        defaultVisible: true,
      },
      options
    );
    if (loadError) {
      throw new Error(`Failed to load ArcGIS layer ${layer.label}`);
    }
    const features = await queryExternalLayerFeatures(
      layers,
      boundary ? { geometry: boundary, spatialRelationship: 'intersects' } : {}
    );
    return features.map((feature) => {
      // Features are queried in WGS84; polygons/lines use their extent centre.
      const point =
        feature.geometry?.type === 'point'
          ? (feature.geometry as __esri.Point)
          : feature.geometry?.extent?.center;
      const extent =
        feature.geometry && feature.geometry.type !== 'point'
          ? feature.geometry.extent
          : null;
      return {
        attributes: feature.attributes ?? {},
        longitude: point?.longitude ?? point?.x ?? null,
        latitude: point?.latitude ?? point?.y ?? null,
        extent: extent
          ? [extent.xmin, extent.ymin, extent.xmax, extent.ymax]
          : null,
        layerTitle: feature.layer?.title ?? null,
      };
    });
  };

  const fetchLayerResult = async (
    layer: LayerAnalyticsSelectedLayer
  ): Promise<LayerAnalyticsResult> => {
    const features = await fetchLayerFeatures(layer);
    return buildLayerAnalyticsResult({
      layerValue: layer.value,
      label: layer.label,
      features,
    });
  };

  const handleRunAnalytics = async () => {
    setIsRunning(true);
    setRunError(null);
    const layersToRun = selectedLayers;
    const settled = await Promise.allSettled(layersToRun.map(fetchLayerResult));
    const failedLayers = layersToRun.filter((layer, index) => {
      const outcome = settled[index];
      if (outcome.status === 'fulfilled') return false;
      observability.captureException(outcome.reason, {
        layerValue: layer.value,
      });
      return true;
    });
    setIsRunning(false);
    // A failed layer is not an empty layer: keep the previous snapshot rather
    // than persisting "0 features" for it.
    if (failedLayers.length > 0) {
      setRunError(
        `Could not load ${failedLayers
          .map((layer) => layer.label)
          .join(', ')}. Previous results were kept; try again.`
      );
      return;
    }
    const nextResults = settled.map(
      (outcome) =>
        (outcome as PromiseFulfilledResult<LayerAnalyticsResult>).value
    );
    const nextRanAt = new Date().toISOString();
    setResults(nextResults);
    setRanAt(nextRanAt);
    saveContent({
      version: 2,
      selectedLayers: layersToRun,
      ranAt: nextRanAt,
      results: nextResults,
    });
  };

  // The draft is shown read-only in the modal (structured content cannot be
  // rebuilt from edited HTML), so the modal's edited content always equals
  // the serialized conflictContent.
  const handleOverrideConflict = (latestConflictVersionId: string) => {
    setConflict(null);
    setConflictContent(null);
    if (conflictContent) {
      baseVersionIdRef.current = latestConflictVersionId;
      saveContent(conflictContent);
    }
  };

  const handleKeepExisting = () => {
    baseVersionIdRef.current = conflict?.latestVersionId ?? latestVersionId;
    setConflict(null);
    setConflictContent(null);
    setSelectedLayers(savedContent.selectedLayers);
    setResults(savedContent.results);
    setRanAt(savedContent.ranAt);
    onUpdated?.();
  };

  return (
    <div className="reports-section">
      <div className="d-flex flex-column gap-3">
        <ComboboxField
          multiple
          label="Hazard layers"
          placeholder="Select wildfire / hurricane layers…"
          emptyMessage={
            isLoadingOptions ? 'Loading layers…' : 'No hazard layers found.'
          }
          options={comboboxOptions}
          value={selectedLayers.map((layer) => layer.value)}
          onChange={handleSelectionChange}
          disabled={isRunning}
        />
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            onClick={handleRunAnalytics}
            disabled={
              selectedLayers.length === 0 || isRunning || isLoadingBoundary
            }
          >
            Run Analytics
          </Button>
          <ArcGisConnectButton />
          {isRunning && (
            <em className="text-muted small">Running analytics…</em>
          )}
        </div>
        {runError && <em className="text-danger small">{runError}</em>}
        {!isLoadingBoundary && !boundary && (
          <em className="text-muted small">{NO_BOUNDARY_MESSAGE}</em>
        )}
        <LayerAnalyticsResults
          results={results}
          emptyMessage="No analytics run yet"
          onZoomToTarget={handleZoomToTarget}
          canZoomToTarget={isMapVisible && !!mapView}
        />
      </div>
      <SitrepSectionConflictModal
        show={!!conflict}
        conflict={conflict}
        draftContent={
          conflictContent ? serializeLayerAnalyticsContent(conflictContent) : ''
        }
        onKeepExisting={handleKeepExisting}
        onOverride={handleOverrideConflict}
        isOverriding={isSaving}
        isDraftReadOnly
      />
    </div>
  );
};

const LayerAnalyticsSection: React.FC<LayerAnalyticsSectionProps> = ({
  section,
  sitrepId,
  canEdit = false,
  onUpdated,
  readOnly = false,
}) => {
  const sectionData = section.section_versions?.[0]?.section_data;
  const savedContent = useMemo(
    () => parseLayerAnalyticsContent(sectionData),
    [sectionData]
  );

  if (readOnly || !canEdit || !sitrepId) {
    return (
      <div className="reports-section">
        <LayerAnalyticsResults
          results={savedContent.results}
          emptyMessage="No analytics run yet"
        />
      </div>
    );
  }

  return (
    <LayerAnalyticsEditor
      section={section}
      sitrepId={sitrepId}
      savedContent={savedContent}
      onUpdated={onUpdated}
    />
  );
};

export default LayerAnalyticsSection;
